import * as React from 'react'
import { ReactNode } from 'react'
import {
  faCalendar,
  faClock,
  faArrowRight,
} from '@fortawesome/pro-regular-svg-icons'
import { mtc } from '../../../utils/style-helpers'
import { Button } from '../../atoms/button'
import { Card, CardColors } from '../../atoms/card'
import { Heading, HeadingVariants } from '../../atoms/heading'
import { IconText } from '../../molecules/icon-text'
import { Image } from '../../atoms/image'
import { Pill, PillColor } from '../../atoms/pill'
import { cardStyles } from './styles'

/** Status color variants mapping to design specs */
export enum YearEndTaskCardStatusColor {
  MOSS = 'moss',
  ORANGE = 'orange',
  RED = 'red',
}

export interface YearEndTaskCardProps {
  /** Status badge text (e.g., "In Progress") - auto-uppercased */
  statusText: string
  /** Status badge color variant */
  statusColor: YearEndTaskCardStatusColor
  /** Due date text (e.g., "Due Jan 31") - optional */
  dueDate?: string
  /** Card title */
  title: string
  /** Time estimate text (e.g., "25 minutes") - optional */
  timeEstimate?: string
  /** Image URL for top-right illustration */
  imageSrc?: string
  /** Image alt text */
  imageAlt?: string
  /** Flexible content area (description, alerts, lists, etc.) */
  children?: ReactNode
  /** CTA button text */
  ctaText?: string
  /** CTA button click handler */
  onCtaClick?: () => void
  /** Show decorative border around card (default: false) */
  bordered?: boolean
  /** Additional className for the card container */
  className?: string
}

// Map status color enum to Pill color prop
const pillColorMap: Record<YearEndTaskCardStatusColor, PillColor> = {
  [YearEndTaskCardStatusColor.MOSS]: 'olive',
  [YearEndTaskCardStatusColor.ORANGE]: 'orange',
  [YearEndTaskCardStatusColor.RED]: 'red',
}

export const YearEndTaskCard = React.forwardRef<
  HTMLDivElement,
  YearEndTaskCardProps
>(
  (
    {
      statusText,
      statusColor,
      dueDate,
      title,
      timeEstimate,
      imageSrc,
      imageAlt,
      children,
      ctaText,
      onCtaClick,
      bordered = false,
      className,
    },
    ref
  ) => {
    return (
      <Card
        ref={ref}
        color={CardColors.PRIORITY}
        className={mtc(cardStyles({ bordered }), className)}
      >
        <div className="flex justify-between gap-6">
          {/* Left side: content */}
          <div className="flex flex-col gap-4 flex-1">
            {/* Status & Due Date badges row */}
            <div className="flex flex-wrap gap-3 items-center">
              <Pill color={pillColorMap[statusColor]}>
                {statusText.toUpperCase()}
              </Pill>
              {dueDate && (
                <IconText icon={faCalendar} size="sm" color="text-neutral-700">
                  {dueDate}
                </IconText>
              )}
            </div>

            {/* Title */}
            <Heading variant={HeadingVariants.H3}>{title}</Heading>

            {/* Time estimate */}
            {timeEstimate && (
              <IconText icon={faClock} size="xs" color="text-neutral-600">
                {timeEstimate}
              </IconText>
            )}

            {/* Children content area */}
            {children && <div className="flex flex-col gap-4">{children}</div>}

            {/* CTA Button */}
            {ctaText && (
              <Button onClick={onCtaClick} iconRight={faArrowRight}>
                {ctaText}
              </Button>
            )}
          </div>

          {/* Right side: Image */}
          {imageSrc && (
            <Image
              src={imageSrc}
              alt={imageAlt || ''}
              className="w-20 h-20 object-contain shrink-0"
            />
          )}
        </div>
      </Card>
    )
  }
)

YearEndTaskCard.displayName = 'YearEndTaskCard'
